import {
    Announced, DetailsListLayoutMode, Dropdown, IColumn, IconButton, IDropdownOption, IStackTokens,
    ITextFieldStyles, mergeStyles, SelectionMode, ShimmeredDetailsList,
    Stack, TextField,
} from "@fluentui/react";
import { Badge, Spinner } from "@fluentui/react-components";
import { ApplicationInsights, SeverityLevel } from "@microsoft/applicationinsights-web";
import * as React from "react";
import { IServiceDataType } from "../../App";
import HttpClient from "../../services/HttpService/HttpClient";
import HttpService from "../../services/HttpService/HttpService";
import { trackException } from "../AppInsights/LoggingHelper";
import { TimeZone } from "../Layout/Header";
import { UnauthorizedMessage } from "../Shared/AppConstants";
import { RootContext } from "../Stores/RootStore";

// Styles //

const textFieldStyles: Partial<ITextFieldStyles> = { root: { maxWidth: "300px", minWidth: "150px" } };

const mainDivStyles = mergeStyles({
    display: "block",
    margin: "auto",
    overflow: "auto",
});

const stackItemsPadding = mergeStyles({
    padding: "0px 20px 0px 0px",
});

const stackTokens: IStackTokens = {
    childrenGap: "0 0",
};

const expandedRowStyles = mergeStyles({
    background: "#eff6fc",
    padding: "12px 24px 12px 48px",
    borderTop: "1px solid #edebe9",
    borderBottom: "1px solid #edebe9",
});

const multilineCell = mergeStyles({
    whiteSpace: "pre-line",
});

// Interfaces //

export interface IJobHistoryProps {
    refreshData: number;
    timezone: TimeZone | undefined;
    selectedService: IServiceDataType | undefined;
    appInsights: ApplicationInsights | undefined;
}

export interface IJobHistoryServerItem {
    fqdn: string;
    patchedDate: string;
    patchStatus: string;
    successful: boolean;
    kbs: string;
    title: string;
    remarks: string;
}

export interface IJobHistoryItem {
    jobId: string;
    jobName: string;
    serviceName: string;
    serviceTreeGUID: string;
    scheduledDateTime: string;
    serversTotal: number;
    serversPatched: number;
    serversFailed: number;
    isExpanded: boolean;
    serversLoaded: boolean;
    serversLoading: boolean;
    servers: IJobHistoryServerItem[];
}

interface IDetailsListJobHistoryState {
    items: IJobHistoryItem[];
    allItems: IJobHistoryItem[];
    jobsLoaded: boolean;
    jobFilterValue: string;
    serviceNameFilter: string;
    selectedServiceGUID: string;
    selectedServiceName: string;
    columns: IColumn[];
    httpService: HttpClient;
    announcedItems: JSX.Element | undefined;
    nestedSortColumn: keyof IJobHistoryServerItem;
    nestedSortDescending: boolean;
}

const noJobsMessage = "No jobs available for the selected service.";

// Format an ISO timestamp as "YYYY-MM-DD HH:mm" in the user's local time.
// Returns "" for null/empty/invalid input.
function formatDateTime(value: string | null | undefined): string {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export class DetailsListJobHistory extends React.Component<IJobHistoryProps, IDetailsListJobHistoryState> {
    public static contextType: React.Context<any> = RootContext;
    private _columns: IColumn[];
    private _timeZoneDisplay: string;

    constructor(props: IJobHistoryProps) {
        super(props);

        const currentDate = new Date();
        const tzHours = (props.timezone?.standardName !== undefined && props.timezone?.standardName !== null)
            ? props.timezone!.offset! / 60
            : -currentDate.getTimezoneOffset() / 60;
        this._timeZoneDisplay = tzHours > 0 ? "+" + tzHours.toString() : tzHours.toString();

        this._columns = [
            {
                key: "expand", name: "", fieldName: "expand", minWidth: 28, maxWidth: 28,
                isResizable: false, onRender: this._renderExpandCell,
            },
            { key: "jobName", name: "Job Name", fieldName: "jobName", minWidth: 160, maxWidth: 260, isResizable: true, onColumnClick: this._onColumnClick, isSorted: true },
            { key: "serviceName", name: "Service Name", fieldName: "serviceName", minWidth: 140, maxWidth: 200, isResizable: true, onColumnClick: this._onColumnClick },
            {
                key: "scheduledDateTime", name: "Scheduled (UTC" + this._timeZoneDisplay + ")",
                fieldName: "scheduledDateTime", minWidth: 140, maxWidth: 180, isResizable: true,
                onColumnClick: this._onColumnClick,
                onRender: (item?: IJobHistoryItem) => <span>{formatDateTime(item?.scheduledDateTime)}</span>,
            },
            { key: "serversTotal", name: "Total", fieldName: "serversTotal", minWidth: 60, maxWidth: 80, isResizable: true, onColumnClick: this._onColumnClick },
            { key: "serversPatched", name: "Patched", fieldName: "serversPatched", minWidth: 70, maxWidth: 90, isResizable: true, onColumnClick: this._onColumnClick },
            { key: "serversFailed", name: "Failed", fieldName: "serversFailed", minWidth: 60, maxWidth: 80, isResizable: true, onColumnClick: this._onColumnClick },
        ];

        this.state = {
            items: [],
            allItems: [],
            jobsLoaded: true,
            jobFilterValue: "",
            serviceNameFilter: "",
            selectedServiceGUID: props.selectedService?.ServiceTreeGUID ?? "",
            selectedServiceName: props.selectedService?.ServiceName ?? "",
            columns: this._columns,
            httpService: HttpService(props.appInsights),
            announcedItems: undefined,
            nestedSortColumn: "patchStatus",
            nestedSortDescending: false,
        };
    }

    public componentDidMount() {
        this.fetchAndPopulate();
    }

    public componentDidUpdate(prevProps: IJobHistoryProps, prevState: IDetailsListJobHistoryState) {
        const { jobFilterValue, serviceNameFilter } = this.state;
        const { refreshData } = this.props;

        if (refreshData > 0 && prevProps.refreshData !== refreshData) {
            this.setState({ items: [], allItems: [], jobsLoaded: false });
            this.fetchAndPopulate();
        }

        if (prevState.jobFilterValue !== jobFilterValue || prevState.serviceNameFilter !== serviceNameFilter) {
            this.applyFilters();
        }
    }

    public render(): JSX.Element {
        const { items, allItems, jobsLoaded, jobFilterValue, serviceNameFilter, announcedItems } = this.state;

        const serviceNameOptions: IDropdownOption[] = [
            { key: "", text: "All services" },
            ...Array.from(new Set(allItems.map(j => j.serviceName).filter(n => !!n)))
                .sort((a, b) => a.localeCompare(b))
                .map(name => ({ key: name, text: name })),
        ];

        return (
            <div className={mainDivStyles}>
                <div aria-live="polite" aria-atomic="true" id="refreshJobHistoryAnnouncement" style={{ position: "absolute", left: "-9999px" }}>
                    {announcedItems}
                </div>

                <Stack horizontal tokens={stackTokens} wrap>
                    <Stack.Item className={stackItemsPadding}>
                        <Dropdown
                            id="jobHistoryServiceDropdown"
                            label="Select a Service"
                            placeholder={jobsLoaded ? "All services" : "Loading services..."}
                            options={serviceNameOptions}
                            selectedKey={serviceNameFilter}
                            disabled={!jobsLoaded || allItems.length === 0}
                            onChange={(_, opt) => this.setState({ serviceNameFilter: (opt?.key as string) ?? "" })}
                            styles={{ root: { maxWidth: "300px", minWidth: "220px" } }}
                        />
                    </Stack.Item>
                    <Stack.Item className={stackItemsPadding}>
                        <TextField
                            id="jobHistoryNameFilter"
                            label="Job Name"
                            placeholder="Filter by job name"
                            value={jobFilterValue}
                            onChange={(_, v) => this.setState({ jobFilterValue: v ?? "" })}
                            styles={textFieldStyles}
                        />
                    </Stack.Item>
                </Stack>

                <div style={{ marginTop: "12px" }}>
                    <ShimmeredDetailsList
                        items={items}
                        columns={this.state.columns}
                        selectionMode={SelectionMode.none}
                        setKey="jobhistory"
                        layoutMode={DetailsListLayoutMode.justified}
                        ariaLabel="Job history list"
                        enableShimmer={!jobsLoaded}
                        onRenderRow={this._onRenderRow}
                    />
                </div>

                {jobsLoaded && items.length === 0 && (
                    <div role="status" aria-live="polite" style={{ padding: "16px", color: "#605e5c" }}>{noJobsMessage}</div>
                )}
            </div>
        );
    }

    // --- Rendering helpers ---

    private _renderExpandCell = (item?: IJobHistoryItem): JSX.Element => {
        if (!item) return <></>;
        return (
            <IconButton
                iconProps={{ iconName: item.isExpanded ? "ChevronDown" : "ChevronRight" }}
                ariaLabel={item.isExpanded ? "Collapse servers for " + item.jobName : "Expand servers for " + item.jobName}
                aria-expanded={item.isExpanded}
                onClick={() => this._toggleExpand(item)}
            />
        );
    }

    private _onRenderRow = (props?: any, defaultRender?: (props?: any) => JSX.Element | null): JSX.Element | null => {
        if (!props || !defaultRender) return null;
        const item: IJobHistoryItem = props.item;
        return (
            <>
                {defaultRender(props)}
                {item.isExpanded && (
                    <div className={expandedRowStyles} role="region" aria-label={"Servers for job " + item.jobName}>
                        {item.serversLoading ? (
                            <Spinner size="small" label="Loading servers..." />
                        ) : item.servers.length === 0 ? (
                            <span>No servers found for this job.</span>
                        ) : (
                            this._renderServersTable(item)
                        )}
                    </div>
                )}
            </>
        );
    }

    private _onNestedColumnClick = (column: keyof IJobHistoryServerItem): void => {
        const { nestedSortColumn, nestedSortDescending, items } = this.state;
        if (nestedSortColumn === column) {
            this.setState({ nestedSortDescending: !nestedSortDescending, items: items.slice() });
        } else {
            this.setState({ nestedSortColumn: column, nestedSortDescending: false, items: items.slice() });
        }
    }

    private _sortedServers(servers: IJobHistoryServerItem[]): IJobHistoryServerItem[] {
        const { nestedSortColumn, nestedSortDescending } = this.state;
        return servers.slice().sort((a, b) => {
            const av = ((a[nestedSortColumn] ?? "") as string).toLowerCase();
            const bv = ((b[nestedSortColumn] ?? "") as string).toLowerCase();
            if (av < bv) return nestedSortDescending ? 1 : -1;
            if (av > bv) return nestedSortDescending ? -1 : 1;
            return 0;
        });
    }

    private _renderSortableHeader(label: string, column: keyof IJobHistoryServerItem, extraStyle?: React.CSSProperties): JSX.Element {
        const { nestedSortColumn, nestedSortDescending } = this.state;
        const isSorted = nestedSortColumn === column;
        const arrow = isSorted ? (nestedSortDescending ? " \u2193" : " \u2191") : "";
        return (
            <th
                scope="col"
                style={{ textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap", cursor: "pointer", userSelect: "none", ...extraStyle }}
                onClick={() => this._onNestedColumnClick(column)}
                aria-sort={isSorted ? (nestedSortDescending ? "descending" : "ascending") : undefined}
            >
                {label}{arrow}
            </th>
        );
    }

    private _renderServersTable(item: IJobHistoryItem): JSX.Element {
        const sortedServers = this._sortedServers(item.servers);
        return (
            <table style={{ width: "100%", borderCollapse: "collapse" }} aria-label={"Server detail for " + item.jobName}>
                <thead>
                    <tr style={{ background: "#f3f2f1" }}>
                        {this._renderSortableHeader("Asset Name", "fqdn")}
                        {this._renderSortableHeader("Patched Date", "patchedDate")}
                        {this._renderSortableHeader("Status", "patchStatus")}
                        {this._renderSortableHeader("KBs", "kbs")}
                        {this._renderSortableHeader("Title", "title")}
                        {this._renderSortableHeader("Remarks", "remarks", { minWidth: 120 })}
                    </tr>
                </thead>
                <tbody>
                    {sortedServers.map(s => (
                        <tr key={item.jobId + "_" + s.fqdn}>
                            <th scope="row" style={{ textAlign: "left", fontWeight: 400, padding: "6px 8px", verticalAlign: "top" }}>{(s.fqdn || "").split(".")[0]}</th>
                            <td style={{ padding: "6px 8px", verticalAlign: "top" }}>{formatDateTime(s.patchedDate)}</td>
                            <td style={{ padding: "6px 8px", verticalAlign: "top" }}>
                                <Badge
                                    appearance="filled"
                                    color={this._statusColor(s.patchStatus)}
                                    size="medium"
                                    style={{ fontWeight: 600, fontSize: 12, padding: "2px 8px", whiteSpace: "nowrap" }}
                                >
                                    {s.patchStatus}
                                </Badge>
                            </td>
                            <td className={multilineCell} style={{ padding: "6px 8px", verticalAlign: "top" }}>{s.kbs}</td>
                            <td className={multilineCell} style={{ padding: "6px 8px", verticalAlign: "top" }}>{s.title}</td>
                            <td className={multilineCell} style={{ padding: "6px 8px", verticalAlign: "top" }}>{s.remarks}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    private _statusColor(status: string): "success" | "danger" | "subtle" | "informative" | "important" | "warning" {
        switch ((status || "").toLowerCase()) {
            case "patched": return "success";
            case "failed": return "danger";
            case "not patched": return "important";
            default: return "informative";
        }
    }

    // --- Actions ---

    private _toggleExpand(item: IJobHistoryItem): void {
        const next = this.state.items.map(i => i.jobId === item.jobId ? { ...i, isExpanded: !i.isExpanded } : i);
        this.setState({ items: next });
    }

    private applyFilters(): void {
        const { allItems, jobFilterValue, serviceNameFilter } = this.state;
        const nameQuery = jobFilterValue.trim().toLowerCase();
        const filtered = allItems.filter(j => {
            const matchesName = nameQuery === "" || j.jobName.toLowerCase().indexOf(nameQuery) >= 0;
            const matchesService = serviceNameFilter === "" || j.serviceName === serviceNameFilter;
            return matchesName && matchesService;
        });
        this.setState({
            items: filtered,
            announcedItems: <Announced message={`${filtered.length} jobs displayed`} />,
        });
    }

    // --- Data ---

    public fetchAndPopulate(): void {
        const { state } = this.context as { state: any };
        const { httpService } = this.state;

        this.setState({ jobsLoaded: false });

        httpService.get({
            url: "api/JobsTracking/patchsummary",
            token: state.AuthStore.Token,
            params: {},
        }).then((response: any) => {
            const data: any = response?.data;
            const rawList = Array.isArray(data) ? data : (data?.jobs ?? data?.Jobs ?? []);
            const jobs: IJobHistoryItem[] = rawList.map((j: any) => {
                const rawServers: any[] = (j.servers ?? j.Servers ?? []) as any[];
                const mapped: IJobHistoryServerItem[] = rawServers
                    .map(s => ({
                        fqdn: s.fqdn ?? s.FQDN ?? "",
                        patchedDate: s.patchedDate ?? s.PatchedDate ?? "",
                        patchStatus: s.patchStatus ?? s.PatchStatus ?? "",
                        successful: (s.successful ?? s.Successful) === true,
                        kbs: s.kbs ?? s.kBs ?? s.KBs ?? "",
                        title: s.title ?? s.Title ?? "",
                        remarks: s.remarks ?? s.Remarks ?? "",
                    }))
                    // Drop records with no identifying FQDN — they are noise rows that
                    // otherwise show up as orphan status badges with no other data.
                    .filter(s => (s.fqdn || "").trim().length > 0);
                // Dedupe multiple entries per server (one server can appear once per KB).
                // Show a single row per FQDN with an aggregate status:
                //   Failed   -> any record failed
                //   Patched  -> every record succeeded
                //   Not Patched -> otherwise
                const grouped = new Map<string, IJobHistoryServerItem[]>();
                mapped.forEach(s => {
                    const key = (s.fqdn || "").toLowerCase();
                    const bucket = grouped.get(key) ?? [];
                    bucket.push(s);
                    grouped.set(key, bucket);
                });
                const uniq = (vals: string[]): string =>
                    Array.from(new Set(vals.map(v => (v ?? "").trim()).filter(v => v.length > 0))).join(", ");
                const servers: IJobHistoryServerItem[] = Array.from(grouped.values()).map(group => {
                    const anyFailed = group.some(g => (g.patchStatus || "").toLowerCase() === "failed");
                    const allSucceeded = group.length > 0 && group.every(g => g.successful === true);
                    let rollupStatus: string;
                    let rollupSuccessful: boolean;
                    if (anyFailed) {
                        rollupStatus = "Failed";
                        rollupSuccessful = false;
                    } else if (allSucceeded) {
                        rollupStatus = "Patched";
                        rollupSuccessful = true;
                    } else {
                        rollupStatus = "Not Patched";
                        rollupSuccessful = false;
                    }
                    const latestPatchedDate = group
                        .map(g => g.patchedDate)
                        .filter(d => !!d)
                        .sort()
                        .pop() ?? "";
                    return {
                        fqdn: group[0].fqdn,
                        patchedDate: latestPatchedDate,
                        patchStatus: rollupStatus,
                        successful: rollupSuccessful,
                        kbs: uniq(group.map(g => g.kbs)),
                        title: uniq(group.map(g => g.title)),
                        remarks: uniq(group.map(g => g.remarks)),
                    };
                });
                const patchedCount = servers.filter(s => s.successful === true).length;
                const failedCount = servers.filter(s => (s.patchStatus || "").toLowerCase() === "failed").length;
                const totalCount = j.serverCount ?? j.ServerCount ?? j.serversTotal ?? j.ServersTotal ?? servers.length;
                return {
                    jobId: j.jobId ?? j.JobId,
                    jobName: j.jobName ?? j.JobName,
                    serviceName: j.serviceName ?? j.ServiceName ?? "",
                    serviceTreeGUID: j.serviceTreeGUID ?? j.ServiceTreeGUID ?? "",
                    scheduledDateTime: j.scheduledDateTime ?? j.ScheduledDateTime
                        ?? j.lastScheduledDateTime ?? j.LastScheduledDateTime ?? "",
                    serversTotal: totalCount,
                    serversPatched: j.serversPatched ?? j.ServersPatched ?? patchedCount,
                    serversFailed: j.serversFailed ?? j.ServersFailed ?? failedCount,
                    isExpanded: false,
                    serversLoaded: servers.length > 0,
                    serversLoading: false,
                    servers,
                };
            });
            this.setState({
                items: jobs,
                allItems: jobs,
                jobsLoaded: true,
                announcedItems: <Announced message={`${jobs.length} jobs loaded`} />,
            });
        }).catch((reason: any) => {
            trackException(this.props.appInsights!, reason, SeverityLevel.Error, "Job History", "fetchAndPopulate", "api/JobsTracking/patchsummary", state.AuthStore, {});
            this.setState({ items: [], allItems: [], jobsLoaded: true });
        });
    }

    // --- Sorting ---

    private _onColumnClick = (_ev: React.MouseEvent<HTMLElement>, column: IColumn): void => {
        const { columns, items } = this.state;
        const newColumns: IColumn[] = columns.slice();
        const currentColumn: IColumn = newColumns.filter(c => column.key === c.key)[0];
        newColumns.forEach((newCol: IColumn) => {
            if (newCol === currentColumn) {
                currentColumn.isSortedDescending = !currentColumn.isSortedDescending;
                currentColumn.isSorted = true;
            } else {
                newCol.isSorted = false;
                newCol.isSortedDescending = true;
            }
        });
        const fieldName = currentColumn.fieldName as keyof IJobHistoryItem;
        const sorted = items.slice().sort((a, b) => {
            const av = (a[fieldName] ?? "") as any;
            const bv = (b[fieldName] ?? "") as any;
            if (av < bv) return currentColumn.isSortedDescending ? 1 : -1;
            if (av > bv) return currentColumn.isSortedDescending ? -1 : 1;
            return 0;
        });
        this.setState({ columns: newColumns, items: sorted });
    }
}
